import React, { useRef, useState } from 'react';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, ComposedChart
} from 'recharts';
import toast from 'react-hot-toast';
import html2canvas from 'html2canvas';
import { getMetaTier, getMetaBadgeClass, getMetaBadgeLabel } from '../../utils/metaColor';

const formatCurrency = (value) => {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
};

const formatNumber = (value) => {
  return new Intl.NumberFormat('pt-BR').format(value);
};

const CurrencyInput = ({ value, onChange, className }) => {
  const [displayValue, setDisplayValue] = React.useState('');

  const parseBRLCurrency = (val) => {
    if (!val) return 0;
    if (typeof val === 'number') return val;
    let cleanStr = val.toString().replace(/[R$\s]/g, '');
    if (cleanStr.includes(',')) {
      cleanStr = cleanStr.replace(/\./g, '').replace(',', '.');
    }
    return parseFloat(cleanStr) || 0;
  };

  const formatNumberToBRLStr = (num) => {
    if (!num || num === 0) return '';
    return new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(num);
  };

  React.useEffect(() => {
    if (value !== undefined && value !== null) {
      if (!displayValue || parseBRLCurrency(displayValue) !== value) {
         setDisplayValue(formatNumberToBRLStr(value));
      }
    } else {
      setDisplayValue('');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const handleChange = (e) => {
    const rawVal = e.target.value;
    if (/^[0-9R$\s.,]*$/.test(rawVal)) {
      setDisplayValue(rawVal);
      onChange(parseBRLCurrency(rawVal));
    } else if (rawVal === '') {
      setDisplayValue('');
      onChange(0);
    }
  };

  const handleBlur = (e) => {
    const parsed = parseBRLCurrency(e.target.value);
    setDisplayValue(formatNumberToBRLStr(parsed));
  };

  return (
    <input
      type="text"
      value={displayValue}
      onChange={handleChange}
      onBlur={handleBlur}
      className={className}
      placeholder="0,00"
    />
  );
};

const FranchiseReportVisualization = ({ reportName, data, updateGoal, onSave, onShare, sharedLink, onBack, isSaving, periodLabel, readOnly = false, clinicSection }) => {
  const containerRef = useRef(null);
  const [exporting, setExporting] = useState(false);

  const handleExportPng = async () => {
    if (!containerRef.current) return;
    setExporting(true);
    try {
      const el = containerRef.current;
      const orig = { width: el.style.width, maxWidth: el.style.maxWidth };
      el.style.width = '1920px';
      el.style.maxWidth = '1920px';
      const canvas = await html2canvas(el, { scale: 2, useCORS: true, windowWidth: 1920 });
      el.style.width = orig.width;
      el.style.maxWidth = orig.maxWidth;
      const link = document.createElement('a');
      link.href = canvas.toDataURL('image/png');
      link.download = `relatorio_franquia_${new Date().toISOString().split('T')[0]}.png`;
      link.click();
    } catch (err) {
      console.error(err);
    } finally {
      setExporting(false);
    }
  };

  // Aggregate Key Metrics
  const aggregated = data.reduce((acc, curr) => {
    acc.spend += curr.totals.spend;
    acc.impressions += curr.totals.impressions;
    acc.reach += curr.totals.reach;
    acc.conversions += curr.totals.conversions;
    acc.leads += curr.totals.leads;
    acc.messages += curr.totals.messages;
    acc.goalTotal += curr.revenueGoal;
    acc.actualTotal += curr.actualRevenue;
    return acc;
  }, { spend: 0, impressions: 0, reach: 0, conversions: 0, leads: 0, messages: 0, goalTotal: 0, actualTotal: 0 });

  // Avg Frequency (if reach > 0)
  const avgFrequency = aggregated.reach > 0 ? (aggregated.impressions / aggregated.reach).toFixed(2) : 0;
  
  // Goal Achieved Percentage (Average of averages or Total Actual / Total Goal)
  const goalAchievedPercent = aggregated.goalTotal > 0 ? ((aggregated.actualTotal / aggregated.goalTotal) * 100).toFixed(2) : 0;

  // Chart Data format
  const chartData = data.map(d => {
    const costPerConv = d.totals.conversions > 0 ? d.totals.spend / d.totals.conversions : 0;
    return {
      name: d.accountName,
      gasto: +(d.totals.spend).toFixed(2),
      conversoes: d.totals.conversions,
      custoConversao: +costPerConv.toFixed(2),
      faturamento: +(d.actualRevenue).toFixed(2),
      porcentagemMeta: d.revenueGoal > 0 ? +((d.actualRevenue / d.revenueGoal) * 100).toFixed(2) : 0
    };
  });

  return (
    <div ref={containerRef} className="animate-fade-in w-full max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <div className="flex gap-4 items-center">
          <button onClick={onBack} disabled={isSaving} className="w-10 h-10 flex items-center justify-center bg-white rounded-full shadow-sm hover:bg-slate-50 text-slate-600 transition-colors">
            <span className="material-symbols-outlined">arrow_back</span>
          </button>
          <div>
            <h2 className="text-3xl font-extrabold font-headline">{reportName}</h2>
            <p className="text-sm font-label text-slate-500 uppercase tracking-widest">{periodLabel} &bull; {data.length} Contas</p>
          </div>
        </div>
        <div className="flex gap-3 items-center flex-wrap">
          {sharedLink && (
            <div className="flex items-center gap-2 bg-surface-container-low px-4 py-2 rounded-full border border-outline-variant/30">
               <a href={sharedLink} target="_blank" rel="noopener noreferrer" className="text-[11px] text-secondary font-mono truncate max-w-[200px] hover:text-primary transition-colors block">
                 {sharedLink}
               </a>
               <button onClick={() => { navigator.clipboard.writeText(sharedLink); toast.success('Link copiado!'); }} className="text-secondary hover:text-primary transition-colors flex items-center" title="Copiar Link">
                 <span className="material-symbols-outlined text-[16px]">content_copy</span>
               </button>
            </div>
          )}
          <button
            onClick={handleExportPng}
            disabled={exporting}
            className="bg-surface-container text-on-surface hover:bg-surface-container-high border border-slate-200 px-6 py-2 rounded-full font-bold shadow-sm transition-all flex items-center gap-2 disabled:opacity-50"
          >
            <span className="material-symbols-outlined text-sm">{exporting ? 'hourglass_empty' : 'image'}</span>
            {exporting ? 'Exportando...' : 'Exportar PNG'}
          </button>
          {onShare && !readOnly && (
            <button
              onClick={onShare}
              disabled={isSaving}
              className="bg-primary/10 text-primary border border-primary/20 hover:bg-primary/20 px-6 py-2 rounded-full font-bold shadow-sm transition-all flex items-center gap-2 disabled:opacity-50"
            >
              <span className="material-symbols-outlined text-sm">share</span> Gerar Link
            </button>
          )}
          {!readOnly && (
            <button 
              onClick={onSave}
              disabled={isSaving}
              className="primary-pill-gradient text-white px-6 py-2 rounded-full font-bold shadow-lg hover:scale-105 transition-all flex items-center gap-2 disabled:opacity-50 disabled:hover:scale-100"
            >
              {isSaving ? <span className="material-symbols-outlined animate-spin text-sm">refresh</span> : <span className="material-symbols-outlined text-sm">save</span>}
              Salvar Relatório
            </button>
          )}
        </div>
      </div>

      {/* Métricas Chave */}
      <section className="mb-10">
        <h3 className="font-headline font-bold text-xl mb-4 flex items-center gap-2"><span className="material-symbols-outlined text-primary">monitoring</span> Métricas Chave Consolidadas</h3>
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
          <div className="bg-white p-5 rounded-2xl shadow-sm border border-slate-100">
             <p className="text-[10px] font-bold uppercase text-slate-400 mb-1">Total Investimento</p>
             <p className="font-headline font-black text-2xl text-slate-800">{formatCurrency(aggregated.spend)}</p>
          </div>
          <div className="bg-white p-5 rounded-2xl shadow-sm border border-slate-100">
             <p className="text-[10px] font-bold uppercase text-slate-400 mb-1">Alcance Total</p>
             <p className="font-headline font-black text-2xl text-slate-800">{formatNumber(aggregated.reach)}</p>
          </div>
          <div className="bg-white p-5 rounded-2xl shadow-sm border border-slate-100">
             <p className="text-[10px] font-bold uppercase text-slate-400 mb-1">Impressões Totais</p>
             <p className="font-headline font-black text-2xl text-slate-800">{formatNumber(aggregated.impressions)}</p>
          </div>
          <div className="primary-pill-gradient p-5 rounded-2xl shadow-lg shadow-primary/20 border border-primary/30 text-white">
             <p className="text-[10px] font-bold uppercase text-white/80 mb-1">Conversões</p>
             <p className="font-headline font-black text-2xl text-white">{formatNumber(aggregated.conversions)} <span className="text-xs font-bold text-white/80 tracking-wide">(Leads: {aggregated.leads} | MSGs: {aggregated.messages})</span></p>
          </div>
          <div className="bg-white p-5 rounded-2xl shadow-sm border border-slate-100">
             <p className="text-[10px] font-bold uppercase text-slate-400 mb-1">Frequência Média</p>
             <p className="font-headline font-black text-2xl text-slate-800">{avgFrequency}</p>
          </div>
          {aggregated.goalTotal > 0 && (
            <div className="bg-gradient-to-br from-green-500 to-green-600 p-5 rounded-2xl shadow-sm text-white">
              <p className="text-[10px] font-bold uppercase text-green-100 mb-1">% de Meta Atingida</p>
              <p className="font-headline font-black text-2xl">{goalAchievedPercent}%</p>
            </div>
          )}
        </div>
      </section>

      {clinicSection && (
        <section className="mb-10">
          {clinicSection}
        </section>
      )}

      {/* Charts Section */}
      <section className="mb-10 grid grid-cols-1 xl:grid-cols-2 gap-8">
        
        {/* Desempenho Contas */}
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100">
          <h3 className="font-headline font-bold mb-6 flex items-center gap-2"><span className="material-symbols-outlined text-primary text-[20px]">bar_chart</span> Desempenho Contas</h3>
          <div className="h-[300px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#64748b' }} tickLine={false} axisLine={false} />
                <YAxis yAxisId="left" tick={{ fontSize: 10, fill: '#64748b' }} tickLine={false} axisLine={false} tickFormatter={(v) => `R$${v}`} />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10, fill: '#64748b' }} tickLine={false} axisLine={false} />
                <Tooltip 
                  contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)' }}
                  labelStyle={{ fontWeight: 'bold', color: '#1e293b', marginBottom: '4px' }}
                  formatter={(value, name) => [name === 'Gasto Total (R$)' ? formatCurrency(value) : value, name]}
                />
                <Legend iconType="circle" wrapperStyle={{ fontSize: '12px' }}/>
                <Bar yAxisId="left" dataKey="gasto" name="Gasto Total (R$)" fill="#cd19f9" radius={[4, 4, 0, 0]} />
                <Line yAxisId="right" type="monotone" dataKey="conversoes" name="Conversões" stroke="#0ea5e9" strokeWidth={3} dot={{ r: 4, strokeWidth: 2 }} activeDot={{ r: 6 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Conversões vs Custo vs Faturamento */}
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100">
          <h3 className="font-headline font-bold mb-6 flex items-center gap-2"><span className="material-symbols-outlined text-primary text-[20px]">show_chart</span> Conversões x Faturamento</h3>
          <div className="h-[300px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#64748b' }} tickLine={false} axisLine={false} />
                <YAxis yAxisId="left" tick={{ fontSize: 10, fill: '#64748b' }} tickLine={false} axisLine={false} tickFormatter={(v) => `R$${v}`} />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10, fill: '#64748b' }} tickLine={false} axisLine={false} />
                <Tooltip 
                  contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)' }}
                  labelStyle={{ fontWeight: 'bold', color: '#1e293b', marginBottom: '4px' }}
                  formatter={(value, name) => [
                     name === 'Qtd Conversões' ? value : formatCurrency(value), 
                     name
                  ]}
                />
                <Legend iconType="circle" wrapperStyle={{ fontSize: '12px' }}/>
                <Bar yAxisId="left" dataKey="faturamento" name="Faturamento Realizado (R$)" fill="#10b981" radius={[4, 4, 0, 0]} />
                <Line yAxisId="left" type="monotone" dataKey="custoConversao" name="Custo / Conv (R$)" stroke="#ef4444" strokeWidth={3} />
                <Line yAxisId="right" type="monotone" dataKey="conversoes" name="Qtd Conversões" stroke="#0ea5e9" strokeWidth={3} activeDot={{ r: 6 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>

      </section>

      {/* Tabela Detalhes */}
      <section className="mb-10 bg-white p-6 rounded-2xl shadow-sm border border-slate-100">
         <h3 className="font-headline font-bold mb-6 flex items-center gap-2"><span className="material-symbols-outlined text-primary text-[20px]">table_chart</span> Detalhes por Conta</h3>
         <div className="overflow-x-auto">
            <table className="w-full text-left text-sm font-body">
               <thead className="bg-slate-50 text-slate-500 uppercase font-label tracking-widest text-[10px]">
                  <tr>
                     <th className="px-4 py-3 rounded-tl-xl rounded-bl-xl font-bold">Conta</th>
                     <th className="px-4 py-3 font-bold">Investimento</th>
                     <th className="px-4 py-3 font-bold">Impressões</th>
                     <th className="px-4 py-3 font-bold">Alcance</th>
                     <th className="px-4 py-3 font-bold">ConvIniciadas</th>
                     <th className="px-4 py-3 font-bold">Leads</th>
                     <th className="px-4 py-3 font-bold">Custo/Conv</th>
                  </tr>
               </thead>
               <tbody className="divide-y divide-slate-100">
                  {data.map(d => {
                     const costPerConv = d.totals.conversions > 0 ? d.totals.spend / d.totals.conversions : 0;
                     return (
                        <tr key={d.accountId} className="hover:bg-slate-50 transition-colors group">
                           <td className="px-4 py-4 font-bold text-slate-800">{d.accountName}</td>
                           <td className="px-4 py-4">{formatCurrency(d.totals.spend)}</td>
                           <td className="px-4 py-4">{formatNumber(d.totals.impressions)}</td>
                           <td className="px-4 py-4">{formatNumber(d.totals.reach)}</td>
                           <td className="px-4 py-4">{formatNumber(d.totals.messages)}</td>
                           <td className="px-4 py-4">{formatNumber(d.totals.leads)}</td>
                           <td className="px-4 py-4 font-bold text-slate-700">{formatCurrency(costPerConv)}</td>
                        </tr>
                     )
                  })}
               </tbody>
            </table>
         </div>
      </section>

      {/* Ranking de Unidades */}
      <section className="mt-10 mb-10 bg-white p-6 rounded-2xl shadow-sm border border-slate-100">
         <h3 className="font-headline font-bold mb-2 flex items-center gap-2"><span className="material-symbols-outlined text-primary text-[20px]">leaderboard</span> Ranking de Unidades</h3>
         <p className="text-secondary text-sm mb-6">Unidades ordenadas pela % de meta atingida. O custo neste ranking considera o total investido dividido pela soma de leads e conversas iniciadas.</p>

         {(() => {
            // Faixas: 0-40 vermelho | 41-60 amarelo | 61-80 verde claro | 81-99 verde escuro | 100+ meta batida
            // (getTier/getBadgeClass/getBadgeLabel agora vêm de utils/metaColor — compartilhado com franchise-v2)
            const getTier = getMetaTier;
            const getBadgeClass = getMetaBadgeClass;
            const getBadgeLabel = getMetaBadgeLabel;

            const getCardTheme = (p) => {
               if (p >= 100) return { bg: 'bg-green-50', border: 'border-green-200', label: 'text-green-500', value: 'text-green-700', sub: 'text-green-500', msg: 'Meta da franquia atingida!' };
               if (p >= 81)  return { bg: 'bg-green-50', border: 'border-green-200', label: 'text-green-500', value: 'text-green-700', sub: 'text-green-500', msg: 'Muito próximo da meta' };
               if (p >= 61)  return { bg: 'bg-emerald-50', border: 'border-emerald-200', label: 'text-emerald-500', value: 'text-emerald-700', sub: 'text-emerald-500', msg: 'Em desenvolvimento' };
               if (p >= 41)  return { bg: 'bg-yellow-50', border: 'border-yellow-200', label: 'text-yellow-500', value: 'text-yellow-700', sub: 'text-yellow-500', msg: 'Atenção necessária' };
               return { bg: 'bg-red-50', border: 'border-red-200', label: 'text-red-400', value: 'text-red-600', sub: 'text-red-400', msg: 'Abaixo da meta' };
            };

            const sortedRanking = [...data].sort((a, b) => {
               const pA = a.revenueGoal > 0 ? (a.actualRevenue / a.revenueGoal) * 100 : 0;
               const pB = b.revenueGoal > 0 ? (b.actualRevenue / b.revenueGoal) * 100 : 0;
               return pB - pA;
            });

            const g5 = sortedRanking.filter(d => getTier(d.revenueGoal > 0 ? (d.actualRevenue / d.revenueGoal) * 100 : 0) === 5);
            const g4 = sortedRanking.filter(d => getTier(d.revenueGoal > 0 ? (d.actualRevenue / d.revenueGoal) * 100 : 0) === 4);
            const g3 = sortedRanking.filter(d => getTier(d.revenueGoal > 0 ? (d.actualRevenue / d.revenueGoal) * 100 : 0) === 3);
            const g2 = sortedRanking.filter(d => getTier(d.revenueGoal > 0 ? (d.actualRevenue / d.revenueGoal) * 100 : 0) === 2);
            const g1 = sortedRanking.filter(d => getTier(d.revenueGoal > 0 ? (d.actualRevenue / d.revenueGoal) * 100 : 0) === 1);

            const totalGoal   = data.reduce((s, d) => s + (d.revenueGoal || 0), 0);
            const totalActual = data.reduce((s, d) => s + (d.actualRevenue || 0), 0);
            const totalPercentNum = totalGoal > 0 ? (totalActual / totalGoal) * 100 : 0;
            const theme = getCardTheme(totalPercentNum);

            const renderGroup = (groupData, title, colorClass, icon, bgClass, borderClass) => {
               if (groupData.length === 0) return null;
               const groupTotalGoal   = groupData.reduce((s, d) => s + (d.revenueGoal || 0), 0);
               const groupTotalActual = groupData.reduce((s, d) => s + (d.actualRevenue || 0), 0);
               const groupTotalP      = groupTotalGoal > 0 ? (groupTotalActual / groupTotalGoal) * 100 : 0;

               return (
                  <div className="mb-8 last:mb-0">
                     <h4 className={`font-bold text-sm mb-3 flex items-center gap-2 ${colorClass}`}>
                        <span className="material-symbols-outlined text-[18px]">{icon}</span> {title}
                        <span className="ml-auto text-[11px] font-normal text-slate-500">{groupData.length} unidade{groupData.length !== 1 ? 's' : ''}</span>
                     </h4>
                     <div className={`overflow-x-auto rounded-xl border shadow-sm ${borderClass}`}>
                        <table className="w-full text-left text-sm font-body">
                           <thead className={`text-slate-600 uppercase font-label tracking-widest text-[10px] ${bgClass}`}>
                              <tr>
                                 <th className="px-4 py-3 font-bold w-16">Posição</th>
                                 <th className="px-4 py-3 font-bold">Unidade (Conta)</th>
                                 <th className="px-4 py-3 font-bold">% Atingida</th>
                                 <th className="px-4 py-3 font-bold">Faturamento</th>
                                 <th className="px-4 py-3 font-bold">Meta</th>
                                 <th className="px-4 py-3 font-bold">Total Investido</th>
                                 <th className="px-4 py-3 font-bold">Qtd Leads (Conv+Lead)</th>
                                 <th className="px-4 py-3 font-bold">Custo / Conv</th>
                              </tr>
                           </thead>
                           <tbody className="divide-y divide-slate-100">
                              {groupData.map((d) => {
                                 const p = d.revenueGoal > 0 ? (d.actualRevenue / d.revenueGoal) * 100 : 0;
                                 const totalConversions = d.totals.leads + d.totals.messages;
                                 const costPerConv = totalConversions > 0 ? d.totals.spend / totalConversions : 0;
                                 const globalRank = sortedRanking.findIndex(r => r.accountId === d.accountId) + 1;
                                 return (
                                    <tr key={d.accountId} className="hover:bg-slate-50 transition-colors">
                                       <td className="px-4 py-4 font-bold text-slate-400">{globalRank}º</td>
                                       <td className="px-4 py-4 font-bold text-slate-800">{d.accountName}</td>
                                       <td className="px-4 py-4">
                                          <span className={`px-3 py-1 rounded-full text-xs font-bold ${getBadgeClass(p)}`}>
                                             {getBadgeLabel(p)}
                                          </span>
                                       </td>
                                       <td className="px-4 py-4 text-slate-700 font-medium">{formatCurrency(d.actualRevenue)}</td>
                                       <td className="px-4 py-4 text-slate-500">{formatCurrency(d.revenueGoal)}</td>
                                       <td className="px-4 py-4 text-slate-700">{formatCurrency(d.totals.spend)}</td>
                                       <td className="px-4 py-4 font-bold text-slate-800">
                                          {formatNumber(totalConversions)}
                                          <span className="text-[10px] font-normal text-slate-400 ml-1">({formatNumber(d.totals.messages)} conv + {formatNumber(d.totals.leads)} leads)</span>
                                       </td>
                                       <td className="px-4 py-4 text-slate-700 font-medium">{formatCurrency(costPerConv)}</td>
                                    </tr>
                                 );
                              })}
                              {groupTotalGoal > 0 && (
                                 <tr className={`font-bold border-t-2 ${bgClass}`}>
                                    <td className="px-4 py-3 text-slate-500 text-xs uppercase tracking-wider" colSpan={2}>Total do grupo</td>
                                    <td className="px-4 py-3">
                                       <span className={`px-3 py-1 rounded-full text-xs font-bold ${getBadgeClass(groupTotalP)}`}>
                                          {getBadgeLabel(groupTotalP)}
                                       </span>
                                    </td>
                                    <td className="px-4 py-3 text-slate-800">{formatCurrency(groupTotalActual)}</td>
                                    <td className="px-4 py-3 text-slate-600">{formatCurrency(groupTotalGoal)}</td>
                                    <td className="px-4 py-3" colSpan={3}></td>
                                 </tr>
                              )}
                           </tbody>
                        </table>
                     </div>
                  </div>
               );
            };

            return (
               <div>
                  {/* Cards de totais consolidados */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                     <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                        <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1">Meta Total (Franquia)</p>
                        <p className="font-headline font-black text-xl text-slate-800">{formatCurrency(totalGoal)}</p>
                        <p className="text-[10px] text-slate-400 mt-1">{data.filter(d => d.revenueGoal > 0).length} unidades com meta definida</p>
                     </div>
                     <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                        <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1">Faturamento Total Realizado</p>
                        <p className="font-headline font-black text-xl text-slate-800">{formatCurrency(totalActual)}</p>
                        <p className="text-[10px] text-slate-400 mt-1">Soma de todas as unidades</p>
                     </div>
                     <div className={`rounded-xl p-4 border ${theme.bg} ${theme.border}`}>
                        <p className={`text-[10px] font-bold uppercase tracking-widest mb-1 ${theme.label}`}>% Geral Atingida</p>
                        <p className={`font-headline font-black text-3xl ${theme.value}`}>{getBadgeLabel(totalPercentNum)}</p>
                        <p className={`text-[10px] mt-1 ${theme.sub}`}>{theme.msg}</p>
                     </div>
                  </div>

                  {/* Legenda de estados */}
                  <div className="flex flex-wrap gap-2 mb-6 p-4 bg-slate-50 rounded-xl border border-slate-100 items-center">
                     <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mr-2">Estados:</span>
                     <div className="flex items-center gap-1.5 px-3 py-1.5 bg-green-200 rounded-full">
                        <span className="material-symbols-outlined text-green-900 text-[13px]">verified</span>
                        <span className="text-xs font-bold text-green-900">Meta Batida</span>
                        <span className="text-[10px] text-green-800">≥ 100%</span>
                        <span className="ml-1 bg-green-800 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center">{g5.length}</span>
                     </div>
                     <div className="flex items-center gap-1.5 px-3 py-1.5 bg-green-100 rounded-full">
                        <span className="material-symbols-outlined text-green-700 text-[13px]">trending_up</span>
                        <span className="text-xs font-bold text-green-700">Quase lá</span>
                        <span className="text-[10px] text-green-600">81% – 99%</span>
                        <span className="ml-1 bg-green-600 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center">{g4.length}</span>
                     </div>
                     <div className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 rounded-full border border-emerald-200">
                        <span className="material-symbols-outlined text-emerald-600 text-[13px]">moving</span>
                        <span className="text-xs font-bold text-emerald-600">Em desenvolvimento</span>
                        <span className="text-[10px] text-emerald-500">61% – 80%</span>
                        <span className="ml-1 bg-emerald-500 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center">{g3.length}</span>
                     </div>
                     <div className="flex items-center gap-1.5 px-3 py-1.5 bg-yellow-100 rounded-full">
                        <span className="material-symbols-outlined text-yellow-700 text-[13px]">warning</span>
                        <span className="text-xs font-bold text-yellow-700">Alerta</span>
                        <span className="text-[10px] text-yellow-600">41% – 60%</span>
                        <span className="ml-1 bg-yellow-500 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center">{g2.length}</span>
                     </div>
                     <div className="flex items-center gap-1.5 px-3 py-1.5 bg-red-100 rounded-full">
                        <span className="material-symbols-outlined text-red-600 text-[13px]">error</span>
                        <span className="text-xs font-bold text-red-700">Atenção Necessária</span>
                        <span className="text-[10px] text-red-500">0% – 40%</span>
                        <span className="ml-1 bg-red-500 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center">{g1.length}</span>
                     </div>
                  </div>

                  {renderGroup(g5, "Meta Batida (≥ 100%)",        "text-green-800",   "verified",    "bg-green-200",    "border-green-200")}
                  {renderGroup(g4, "Quase Lá (81% – 99%)",        "text-green-700",   "trending_up", "bg-green-50",     "border-green-100")}
                  {renderGroup(g3, "Em Desenvolvimento (61%–80%)", "text-emerald-600", "moving",      "bg-emerald-50",   "border-emerald-100")}
                  {renderGroup(g2, "Alerta (41% – 60%)",          "text-yellow-700",  "warning",     "bg-yellow-50",    "border-yellow-100")}
                  {renderGroup(g1, "Atenção Necessária (0%–40%)", "text-red-600",     "error",       "bg-red-50",       "border-red-100")}
               </div>
            );
         })()}
      </section>

      {/* Metas de Faturamento */}
      <section className="bg-surface-container-lowest p-6 rounded-2xl shadow-[0_8px_32px_rgba(205,25,249,0.04)] border border-primary/10">
         <h3 className="font-headline font-bold mb-2 flex items-center gap-2"><span className="material-symbols-outlined text-primary text-[20px]">flag</span> Metas de Faturamento</h3>
         <p className="text-secondary text-sm mb-6">Preencha manualmente os valores para acompanhar o atingimento de metas. Salve as informações no final da página.</p>
         <div className="overflow-x-auto">
            <table className="w-full text-left text-sm font-body">
               <thead className="bg-slate-50 text-slate-500 uppercase font-label tracking-widest text-[10px]">
                  <tr>
                     <th className="px-4 py-3 rounded-tl-xl rounded-bl-xl font-bold">Conta</th>
                     <th className="px-4 py-3 font-bold">Meta de Faturamento (R$)</th>
                     <th className="px-4 py-3 font-bold">Faturamento Realizado (R$)</th>
                     <th className="px-4 py-3 font-bold">% Atingida</th>
                  </tr>
               </thead>
               <tbody className="divide-y divide-slate-100">
                  {data.map(d => {
                     const p = d.revenueGoal > 0 ? (d.actualRevenue / d.revenueGoal) * 100 : 0;
                     const badgeClass = getMetaBadgeClass(p);
                     const badgeLabel = getMetaBadgeLabel(p);
                     return (
                        <tr key={d.accountId} className="hover:bg-slate-50 transition-colors">
                           <td className="px-4 py-4 font-bold text-slate-800">{d.accountName}</td>
                           <td className="px-4 py-4">
                             {readOnly ? (
                               <span className="font-medium text-slate-700 pr-4">{formatCurrency(d.revenueGoal)}</span>
                             ) : (
                               <div className="relative w-40">
                                 <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-bold">R$</span>
                                 <CurrencyInput
                                   value={d.revenueGoal}
                                   onChange={(val) => updateGoal(d.accountId, 'revenueGoal', val)}
                                   className="w-full bg-white px-4 pl-9 py-2 rounded-lg border border-slate-200 focus:ring-2 focus:ring-primary/20 outline-none font-body text-sm text-right"
                                 />
                               </div>
                             )}
                           </td>
                           <td className="px-4 py-4">
                             {readOnly ? (
                               <span className="font-medium text-slate-700 pr-4">{formatCurrency(d.actualRevenue)}</span>
                             ) : (
                               <div className="relative w-40">
                                 <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-bold">R$</span>
                                 <CurrencyInput
                                   value={d.actualRevenue}
                                   onChange={(val) => updateGoal(d.accountId, 'actualRevenue', val)}
                                   className="w-full bg-white px-4 pl-9 py-2 rounded-lg border border-slate-200 focus:ring-2 focus:ring-primary/20 outline-none font-body text-sm text-right"
                                 />
                               </div>
                             )}
                           </td>
                           <td className="px-4 py-4">
                              <span className={`px-3 py-1 rounded-full text-xs font-bold ${badgeClass}`}>{badgeLabel}</span>
                           </td>
                        </tr>
                     );
                  })}
               </tbody>
            </table>
         </div>
      </section>

    </div>
  );
};

export default FranchiseReportVisualization;
